import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

interface Feature {
  icon: string;
  title: string;
  description: string;
}

interface ProcessStep {
  step: number;
  title: string;
  description: string;
}

interface CaseStudy {
  title: string;
  client: string;
  industry: string;
  challenge: string;
  solution: string;
  results: {
    metric: string;
    value: string;
  }[];
  image: string;
}

interface Testimonial {
  name: string;
  role: string;
  company: string;
  content: string;
  avatar: string;
}

@Component({
  selector: 'app-bulk-sms',
  imports: [CommonModule, RouterLink],
  templateUrl: './bulk-sms.html',
  styleUrl: './bulk-sms.css',
  standalone: true
})
export class BulkSms {
  features: Feature[] = [
    {
      icon: 'M13 10V3L4 14h7v7l9-11h-7z',
      title: 'Lightning Fast Delivery',
      description: 'Send thousands of messages per second with our high-speed SMS gateway. Global reach with local delivery speed across 190+ countries.'
    },
    {
      icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
      title: '99.9% Delivery Rate',
      description: 'Industry-leading delivery rates with multiple carrier redundancy. Real-time delivery reports and automated fallback routing ensure your messages reach their destination.'
    },
    {
      icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
      title: 'Personalized Messaging',
      description: 'Use dynamic variables to personalize each message with customer names, order details, and custom data. Increase engagement with targeted, relevant content.'
    },
    {
      icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
      title: 'Advanced Analytics',
      description: 'Track delivery status, open rates, and engagement metrics in real-time. Export detailed reports and integrate analytics with your existing business intelligence tools.'
    },
    {
      icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
      title: 'Scheduled Campaigns',
      description: 'Schedule messages in advance and automate recurring campaigns. Set optimal delivery times based on timezone and customer behavior patterns.'
    },
    {
      icon: 'M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z',
      title: 'Enterprise API',
      description: 'RESTful API with comprehensive documentation and SDKs for all major languages. Webhooks for real-time event notifications and seamless system integration.'
    }
  ];

  process: ProcessStep[] = [
    {
      step: 1,
      title: 'Upload Contacts',
      description: 'Import your contact list via CSV, Excel, or direct API integration. Segment your audience based on demographics, behavior, or custom criteria.'
    },
    {
      step: 2,
      title: 'Create Message',
      description: 'Compose your message using our intuitive editor. Add personalization variables, preview how messages will appear, and ensure optimal character count.'
    },
    {
      step: 3,
      title: 'Configure & Schedule',
      description: 'Set sender ID, choose delivery time, and configure campaign settings. Apply compliance rules and opt-out management automatically.'
    },
    {
      step: 4,
      title: 'Send & Monitor',
      description: 'Launch your campaign and monitor real-time delivery status. Track engagement metrics, delivery reports, and ROI through comprehensive dashboards.'
    }
  ];

  caseStudy: CaseStudy = {
    title: 'E-commerce Order Updates & Flash Sale Notifications',
    client: 'ShopFast Online',
    industry: 'E-commerce & Retail',
    challenge: 'ShopFast needed a reliable SMS solution to send order confirmations, shipping updates, and time-sensitive flash sale notifications to 2 million active customers. Their previous provider had inconsistent delivery rates causing customer complaints and lost sales.',
    solution: 'Implemented HoneyKode\'s Bulk SMS platform with automated triggers for transactional messages and scheduled campaigns for promotional content. Integrated directly with their e-commerce platform for real-time order updates.',
    results: [
      { metric: 'Message Volume', value: '3.5M messages/month' },
      { metric: 'Delivery Rate', value: '99.8%' },
      { metric: 'Customer Satisfaction', value: '47% increase' },
      { metric: 'Flash Sale Conversions', value: '3.2x improvement' },
      { metric: 'Response Time', value: 'Under 5 seconds' },
      { metric: 'Cost Savings', value: '32% reduction' }
    ],
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=500&fit=crop'
  };

  testimonial: Testimonial = {
    name: 'David Martinez',
    role: 'VP of Operations',
    company: 'ShopFast Online',
    content: 'Switching to HoneyKode\'s Bulk SMS service was a game-changer for us. The delivery rates are exceptional, and the real-time integration with our order management system has streamlined our entire customer communication workflow. Our customers now receive instant updates, and we\'ve seen a significant drop in support tickets related to order status inquiries. The platform pays for itself through improved customer satisfaction alone.',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop'
  };
}
